import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Clock, MapPin, Megaphone } from "lucide-react";
import { useApp, scrollToId } from "../context/AppContext";
import { IMAGES } from "../config/images";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const { openBooking, config } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const liveCities = (config.cities || []).filter((c) => c.live).map((c) => c.name).join(" · ");

  return (
    <section id="home" ref={ref} className="relative overflow-hidden pt-[68px]" data-testid="hero-section">
      <div className="dot-grid pointer-events-none absolute inset-0" />
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full opacity-70" aria-hidden="true">
        <div className="h-full w-full rounded-full" style={{ background: "radial-gradient(circle, rgba(124,77,255,0.22) 0%, rgba(124,77,255,0.06) 45%, transparent 70%)" }} />
      </motion.div>

      <div className="container-x relative grid grid-cols-1 items-center gap-14 py-14 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <div className="lg:col-span-6">
          {config.announcement && (
            <motion.span
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="inline-flex items-center gap-2 rounded-full border border-carza/30 bg-carza-dim px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-carza-lite"
              data-testid="hero-announcement"
            >
              <Megaphone size={11} /> {config.announcement}
            </motion.span>
          )}

          <motion.span initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className={`eyebrow ${config.announcement ? "mt-5 block" : ""}`} data-testid="hero-eyebrow">
            Technology-enabled car care
          </motion.span>

          <h1 className="mt-6 font-display text-[2.6rem] font-bold leading-[1.06] tracking-tight text-paper sm:text-6xl lg:text-[4.2rem]" data-testid="hero-headline">
            {["Professional Car Care.", "Delivered to", "Your Doorstep."].map((t, i) => (
              <span key={t} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className={`block ${i === 2 ? "text-carza-lite text-glow" : ""}`}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
            data-testid="hero-subheadline"
          >
            Cleaner cars. Healthier drives. CARZA combines standardized car-care processes, trained service partners and technology to make vehicle cleaning more convenient, reliable and water-efficient.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }} className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={() => openBooking()} className="btn btn-primary px-7 py-4 text-[15px]" data-testid="hero-book-btn">
              Book a Car Care <ArrowRight size={16} />
            </button>
            <button onClick={() => scrollToId("why-carza")} className="btn btn-outline px-7 py-4 text-[15px]" data-testid="hero-explore-btn">
              Why CARZA?
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }} className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-mist-2" data-testid="hero-time-chip">
              <Clock size={12} className="text-carza-lite" /> Wash done in ~30 min
            </span>
            {liveCities && (
              <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-mist-2" data-testid="hero-location-chip">
                <MapPin size={12} className="text-carza-lite" /> Now serving: {liveCities}
              </span>
            )}
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-[560px]"
          >
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-3xl border border-white/10">
              <img
                src={IMAGES.heroPoster}
                alt="A CARZA service partner giving a powerful, minimal-water car wash in India"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
                fetchPriority="high"
                data-testid="hero-image"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
